import { h } from "preact";
import { useEffect, useLayoutEffect, useState } from "preact/hooks";
import { useDispatch, useSelector } from "react-redux";
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
  redirect,
  useHref,
  useLocation,
} from "react-router-dom";

import DocumentsPage from "./pages/DocumentsPage.jsx";
import NotFoundPage from "./pages/NotFoundPage.jsx";
import PartnerPage from "./pages/PartnerPage.jsx";
import PartnersPage from "./pages/PartnersPage.jsx";
import ProfilePage from "./pages/ProfilePage.jsx";
import ProjectPage from "./pages/ProjectPage.jsx";
import ProjectsPage from "./pages/ProjectsPage.jsx";
import ProjectAddPage from "./pages/ProjectAddPage.jsx";
import ProjectEditPage from "./pages/ProjectEditPage.jsx";
import PartnerProgramPage from "./pages/PartnerProgramPage.jsx";
import ReferralPage from "./pages/ReferralPage.jsx";
import RelocationCasePage from "./pages/RelocationCasePage.jsx";
import RelocationCasesPage from "./pages/RelocationCasesPage.jsx";
import SettingsPage from "./pages/SettingsPage.jsx";
import SharedTalentPage from "./pages/SharedTalentPage.jsx";
import ShowcasePage from "./pages/ShowcasePage.jsx";
import TalentDetailPage from "./pages/TalentDetailPage.jsx";
import TalentFavoritesPage from "./pages/TalentFavoritesPage.jsx";
import TalentPoolPage from "./pages/TalentPoolPage.jsx";
import TaskPage from "./pages/TaskPage.jsx";
import TasksPage from "./pages/TasksPage.jsx";
import WhitepaperPage from "./pages/WhitepaperPage.jsx";
import WhitepaperShowPage from "./pages/WhitepaperShowPage.jsx";
import {
  getRouteQueryParams,
  removeTrailingSlash,
  toQueryString,
} from "./helpers.js";
import {
  selectSeeingCompanyPages,
  selectIsLoggedIn,
  selectPreviewMode,
  setGlobalError,
  setLoading,
  createToastMessage,
  selectCanSeeTalentPool,
} from "./store/index";
import tracking from "./tracking/tracking";
import i18n from "./i18n.js";
import CompanyRegistration from "./pages/CompanyRegistration.jsx";
import { PreviewModeContext } from "./contexts.js";

function RedirectToLogin() {
  const href = useHref();

  useEffect(() => {
    window.location.href = `/login?redirect_to=${href}`;
  }, []);

  return null;
}

function Root({ preview }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const [locationBefore, setLocationBefore] = useState(null);

  useEffect(() => {
    dispatch(setLoading(false));
    // Don't track initial page view since that is tracked by the backend already
    if (locationBefore && locationBefore !== window.location.href) {
      tracking.track("page.visit", {
        path: removeTrailingSlash(window.location.href),
      });
    }
    setLocationBefore(window.location.href);
  }, [location]);

  return (
    <PreviewModeContext.Provider value={preview}>
      <Outlet />
    </PreviewModeContext.Provider>
  );
}

export default function AppRoutes() {
  const canSeeCompanyPages = useSelector(selectSeeingCompanyPages);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const isPreviewMode = useSelector(selectPreviewMode);
  const canSeeTalentPool = useSelector(selectCanSeeTalentPool);
  const lastSeen = useSelector(
    (state) => state.talentPool.lastSeen || "talents"
  );
  const dispatch = useDispatch();

  const urlBase = isPreviewMode ? "/preview" : "/app";

  useLayoutEffect(() => {
    const params = getRouteQueryParams(window.location.search);
    let changed = false;
    if (params.error) {
      dispatch(setGlobalError(params.error));
      delete params.error;
      changed = true;
    }
    if (params.success) {
      createToastMessage(dispatch, {
        type: "success",
        message: i18n.t(params.success, params),
      });
      delete params.success;
      changed = true;
    }
    if (changed) {
      redirect(`${window.location.pathname}?${toQueryString(params)}`);
    }
  }, []);

  let router = null;

  if (isPreviewMode) {
    if (canSeeCompanyPages) {
      window.location.href = "/app/talents";
    }

    router = createBrowserRouter(
      createRoutesFromElements(
        <Route path="/" element={<Root preview={true} />}>
          <Route path="/" element={<Navigate to="/talents" replace={true} />} />
          <Route path="/registration" Component={CompanyRegistration} />
          <Route path="/relocation/:userId" Component={RelocationCasePage} />
          <Route path="/relocation" Component={RelocationCasesPage} />
          <Route path="/whitepaper" Component={WhitepaperPage} />
          <Route
            path="/whitepaper/:whitepaperId"
            Component={WhitepaperShowPage}
          />
          <Route path="/talents" element={<TalentPoolPage previewMode />} />
          <Route
            path="/favorites"
            element={<TalentFavoritesPage previewMode />}
          />
          <Route
            path="/talents/:id"
            element={
              <TalentDetailPage
                previewMode
                backUrl={() => `${urlBase}/${lastSeen}`}
              />
            }
          />
          <Route path="/projects/:id" Component={ProjectPage} />
          <Route path="/projects" Component={ProjectsPage} />

          <Route
            path="/:xxx"
            element={<Navigate to="/talents" replace={true} />}
          />
          <Route
            path="/:xxx/*"
            element={<Navigate to="/talents" replace={true} />}
          />
        </Route>
      ),
      { basename: urlBase }
    );
  } else {
    router = canSeeCompanyPages
      ? createBrowserRouter(
          createRoutesFromElements(
            <Route path="/" element={<Root preview={false} />}>
              <Route
                path="/"
                element={<Navigate to="/talents" replace={true} />}
              />
              <Route path="/showcase" Component={ShowcasePage} />

              <Route
                path="/talents"
                element={
                  <TalentPoolPage
                    previewMode={!canSeeTalentPool}
                    backUrl={() => `/${lastSeen}`}
                  />
                }
              />

              <Route
                path="/relocation/:userId"
                Component={RelocationCasePage}
              />
              <Route path="/relocation" Component={RelocationCasesPage} />

              <Route path="/whitepaper" Component={WhitepaperPage} />
              <Route
                path="/whitepaper/:whitepaperId"
                Component={WhitepaperShowPage}
              />
              <Route path="/profile" Component={ProfilePage} />

              <Route path="/projects/add" Component={ProjectAddPage} />
              <Route path="/projects/add/*" Component={ProjectAddPage} />
              <Route path="/projects/:id/edit" Component={ProjectEditPage} />
              <Route path="/projects/:id" Component={ProjectPage} />
              <Route path="/projects" Component={ProjectsPage} />
              <Route
                path="/projects/:projectId/talents/:id"
                element={
                  <TalentDetailPage
                    previewMode={!canSeeTalentPool}
                    backUrl={(params) => `/projects/${params.projectId}`}
                  />
                }
              />

              <Route path="/referral" Component={PartnerProgramPage} />
              <Route path="/referral/*" Component={PartnerProgramPage} />

              <Route path="/settings" Component={SettingsPage} />
              <Route path="/settings/:subPage" Component={SettingsPage} />

              <Route
                path="/talents"
                element={
                  <TalentPoolPage
                    previewMode={!canSeeTalentPool}
                    backUrl={() => `/${lastSeen}`}
                  />
                }
              />
              <Route path="/favorites" Component={TalentFavoritesPage} />
              <Route
                path="/talents/:id"
                element={
                  <TalentDetailPage
                    previewMode={!canSeeTalentPool}
                    backUrl={() => `${urlBase}/${lastSeen}`}
                  />
                }
              />

              <Route path="/marketplace" Component={PartnersPage} />
              <Route path="/marketplace/:partnerId" Component={PartnerPage} />

              <Route path="/shared/:linkId" Component={SharedTalentPage} />

              <Route
                path="/:xxx"
                element={<Navigate to="/talents" replace={true} />}
              />
              <Route
                path="/:xxx/*"
                element={<Navigate to="/talents" replace={true} />}
              />
            </Route>
          ),
          { basename: urlBase }
        )
      : createBrowserRouter(
          createRoutesFromElements(
            <Route path="/" element={<Root preview={false} />}>
              <Route
                path="/"
                element={<Navigate to="/tasks" replace={true} />}
              />

              <Route path="/docs" Component={DocumentsPage} />
              <Route path="/showcase" Component={ShowcasePage} />
              <Route path="/tasks/:taskId" Component={TaskPage} />
              <Route path="/tasks" Component={TasksPage} />
              <Route path="/whitepaper" Component={WhitepaperPage} />
              <Route
                path="/whitepaper/:whitepaperId"
                Component={WhitepaperShowPage}
              />
              {isLoggedIn && <Route path="/profile" Component={ProfilePage} />}
              {isLoggedIn && (
                <Route path="/settings" Component={SettingsPage} />
              )}
              {isLoggedIn && (
                <Route path="/settings/:subPage" Component={SettingsPage} />
              )}
              {isLoggedIn && (
                <Route path="/referral" Component={ReferralPage} />
              )}
              <Route path="/marketplace" Component={PartnersPage} />
              <Route path="/marketplace/:partnerId" Component={PartnerPage} />
              <Route path="/shared/:linkId" Component={SharedTalentPage} />
              {isLoggedIn ? (
                <Route path="/:xxx" Component={NotFoundPage} />
              ) : (
                <Route path="/:xxx" Component={RedirectToLogin} />
              )}
              {isLoggedIn ? (
                <Route path="/:xxx/:yyy" Component={NotFoundPage} />
              ) : (
                <Route path="/:xxx/:yyy" Component={RedirectToLogin} />
              )}
              {isLoggedIn ? (
                <Route path="/:xxx/:yyy/:zzz" Component={NotFoundPage} />
              ) : (
                <Route path="/:xxx/:yyy/:zzz" Component={RedirectToLogin} />
              )}
              <Route
                path="/"
                element={<Navigate to="/tasks" replace={true} />}
              />
            </Route>
          ),
          { basename: urlBase }
        );
  }

  return <RouterProvider router={router} />;
}
